import React, { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  useMediaQuery,
  Button,
  IconButton,
  Chip,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import InstagramIcon from "@mui/icons-material/Instagram";
import FacebookIcon from "@mui/icons-material/Facebook";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import GroupsIcon from "@mui/icons-material/Groups";
import EmailSignupModal from "./EmailSignupModal";
import endorsementsHeader from "../images/endorsements-bg.jpg";

const endorsedOfficials = [
  { name: "Council Chairman Mike Ertel", title: "Baltimore County Council" },
  { name: "Delegate Dana Stein", title: "Maryland House of Delegates" },
  { name: "Delegate Jon Cardin", title: "Maryland House of Delegates" },
  { name: "Delegate Nick Allen", title: "Maryland House of Delegates" },
  { name: "Senator Carl Jackson", title: "Maryland State Senate" },
  { name: "Former Delegate Dan Morhaim", title: "Maryland House of Delegates" },
];

const endorsedOrgs = [
  { name: "Metro Baltimore AFL-CIO", type: "Labor" },
  { name: "American Federation of Teachers (AFT)", type: "Labor" },
  { name: "AFSCME Council 3", type: "Labor" },
  { name: "Mid-Atlantic Pipe Trade", type: "Labor" },
  { name: "Central Baltimore County Democratic Club", type: "Political" },
  { name: "Moms Demand Action", type: "Advocacy", subtitle: "Gun Sense Candidate" },
];

const Endorsements = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [emailOpen, setEmailOpen] = useState(false);

  return (
    <Box sx={{ backgroundColor: "#FFFFFF!important" }}>
      {/* Header */}
      <Box
        sx={{
          // Replace url(...) with your actual header image once imported
          backgroundImage: `radial-gradient(circle at 78% 30%, rgba(0, 55, 75, 0) 0%, #02449be6 100%), url(${endorsementsHeader})`,
          backgroundSize: "cover",
          backgroundPosition: isMobile ? "left" : "top",
          backgroundRepeat: "no-repeat",
          height: isMobile ? "30vh" : "50vh",
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "flex-end",
          textAlign: "center",
          p: isMobile ? 2 : 4,
        }}
      >
        <Typography
          variant="h1"
          sx={{
            color: "white",
            fontWeight: "900!important",
            fontSize: {
              xs: "2.5rem",
              fontSize: isMobile ? "2rem!important" : "4rem!important",
              textTransform: "uppercase",
              fontWeight: "500!important",
            },
          }}
        >
          Endorsements
        </Typography>
      </Box>

      {/* Body */}
      <Box
        p={isMobile ? "0 16px" : "96px 96px 48px"}
        sx={{ borderTop: "1px solid #fff", background: "#02449b" }}
      >
        <Grid container alignItems="flex-start" flexDirection="column" className="section-wrap">

          {/* Intro text */}
          <Grid item xs={12} mb={isMobile ? 3 : 6}>
            <Typography
              variant="body1"
              sx={{
                fontSize: "1.25rem",
                color: "white!important",
              }}
            >
              Mark Brewster has earned the trust and support of elected leaders,
              labor unions, and community organizations across Baltimore County.
              These endorsements reflect a shared commitment to bold, community-first
              leadership for the District.
            </Typography>
          </Grid>

          {/* Elected Officials Section */}
          <Grid
            container
            className="section-wrap"
            mt={0}
            mb={isMobile ? 3 : 0}
          >
            <Grid
              item
              xs={12}
              md={3}
              my={isMobile ? 2 : 0}
              display={isMobile ? "none!important" : "flex"}
              justifyContent="center"
              alignItems="flex-start"
            >
              <Box
                sx={{
                  borderRadius: 2,
                  padding: "1rem",
                  background: "#03449c",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  minWidth: 180,
                }}
              >
                <HowToVoteIcon sx={{ fontSize: "5rem", color: "#ffffff99" }} />
                <Typography
                  sx={{
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "3rem",
                    lineHeight: 1,
                    textAlign: "center",
                  }}
                >
                  {endorsedOfficials.length}
                </Typography>
                <Typography
                  sx={{
                    color: "#ffffffcc",
                    fontSize: "0.9rem",
                    textAlign: "center",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Elected Officials
                </Typography>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              md={7.5}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  mb: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  letterSpacing: "0!important",
                  color: "#FFF!important",
                  fontWeight: "normal!important",
                  fontSize: isMobile ? "1.5rem!important" : "2rem!important",
                }}
              >
                Elected Officials
              </Typography>

              <Grid container spacing={2}>
                {endorsedOfficials.map((official, i) => (
                  <Grid item xs={12} sm={6} key={i}>
                    <Box
                      sx={{
                        borderLeft: "3px solid #ffffff66",
                        pl: 2,
                        py: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          color: "white",
                          fontWeight: "bold",
                          fontSize: "1.1rem",
                          lineHeight: 1.3,
                        }}
                      >
                        {official.name}
                      </Typography>
                      <Typography
                        sx={{
                          color: "#ffffffaa",
                          fontSize: "0.875rem",
                          mt: 0.25,
                        }}
                      >
                        {official.title}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>

          {/* Organizations Section */}
          <Grid
            container
            className="section-wrap"
            my={isMobile ? 2 : 12}
            sx={{
              background: isMobile ? "none" : "#106cb7",
              borderRadius: ".5rem",
            }}
          >
            <Grid
              item
              xs={false}
              my={18}
              sx={{ display: isMobile ? "none" : "initial", order: "1" }}
            />

            <Grid
              item
              xs={12}
              md={7.5}
              mt={isMobile ? 2 : 2}
              sx={{
                order: isMobile ? "3" : "1",
                display: "flex",
                alignItems: "flex-start",
                flexDirection: "column",
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  mt: 3,
                  mb: 3,
                  display: "flex",
                  alignItems: "center",
                  letterSpacing: "0!important",
                  color: "#FFF!important",
                  fontWeight: "normal!important",
                  fontSize: isMobile ? "1.5rem!important" : "2rem!important",
                }}
              >
                Organizations &amp; Unions
              </Typography>

              <Grid container spacing={2}>
                {endorsedOrgs.map((org, i) => (
                  <Grid item xs={12} sm={6} key={i}>
                    <Box
                      sx={{
                        borderLeft: "3px solid #ffffff66",
                        pl: 2,
                        py: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          color: "white",
                          fontWeight: "bold",
                          fontSize: "1.1rem",
                          lineHeight: 1.3,
                        }}
                      >
                        {org.name}
                      </Typography>
                      {org.subtitle && (
                        <Typography
                          sx={{
                            color: "#ffffffaa",
                            fontSize: "0.875rem",
                            mt: 0.25,
                          }}
                        >
                          {org.subtitle}
                        </Typography>
                      )}
                      <Chip
                        label={org.type}
                        size="small"
                        sx={{
                          mt: 0.75,
                          backgroundColor: "#02449b",
                          color: "#ffffffcc",
                          fontSize: "0.75rem",
                          height: "20px",
                        }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            <Grid
              item
              xs={false}
              my={18}
              sx={{ display: isMobile ? "none" : "initial", order: "2" }}
            />

            <Grid
              item
              display={isMobile ? "none!important" : "flex"}
              justifyContent="center"
              flexDirection="column"
              alignItems="flex-end"
              xs={12}
              md={3}
              sx={{
                order: isMobile ? "1" : "3",
                padding: isMobile ? "0" : "1rem",
                maxHeight: "fit-content",
              }}
            >
              <Box
                sx={{
                  borderRadius: 2,
                  padding: "1rem",
                  background: "#02449b",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  minWidth: 180,
                }}
              >
                <GroupsIcon sx={{ fontSize: "5rem", color: "#ffffff99" }} />
                <Typography
                  sx={{
                    color: "white",
                    fontWeight: "bold",
                    fontSize: "3rem",
                    lineHeight: 1,
                    textAlign: "center",
                  }}
                >
                  {endorsedOrgs.length}
                </Typography>
                <Typography
                  sx={{
                    color: "#ffffffcc",
                    fontSize: "0.9rem",
                    textAlign: "center",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Organizations
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Call to action */}
          <Grid item xs={12} mt={isMobile ? 2 : 0} mb={isMobile ? 2 : 6} sx={{ width: "100%" }}>
            <Box
              sx={{
                background: "#03449c",
                borderRadius: ".5rem",
                p: isMobile ? 2 : 4,
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    color: "white!important",
                    fontWeight: "normal",
                    fontSize: isMobile ? "1.25rem!important" : "1.5rem!important",
                    mb: 0.5,
                  }}
                >
                  Want to endorse Mark?
                </Typography>
                <Typography sx={{ color: "#ffffffcc", fontSize: "1rem" }}>
                  Join the movement and add your name to the list of supporters.
                </Typography>
              </Box>
              <Button
                variant="contained"
                target="_blank"
                href="https://docs.google.com/forms/d/e/1FAIpQLSdNtSzRT45jLFTiUVxocJgR-02u_TPmvXu5iL61dPRI98m6UQ/viewform"
                sx={{
                  textTransform: "none",
                  background: "#ffffff!important",
                  color: "#02449b",
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                  px: 3,
                  py: 1.25,
                  "&:hover": { background: "#e8f0fe!important" },
                }}
              >
                Reach Out to Endorse
              </Button>
            </Box>
          </Grid>

        </Grid>
      </Box>

      {/* Footer */}
      <footer>
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          mt={6}
          pb={4}
          spacing={2}
        >
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              sx={{ textTransform: "none", background: "#106cb7!important" }}
              onClick={() => setEmailOpen(true)}
            >
              Join Our Email List
            </Button>
          </Grid>
          <Grid item>
            <IconButton
              component="a"
              href="https://www.instagram.com/mark.brewster_/profilecard/?igsh=MWVrMDN0eWlzY3E0cQ==/mark_brewster"
              target="_blank"
              rel="noopener noreferrer"
            >
              <InstagramIcon sx={{ color: "white" }} />
            </IconButton>
            <IconButton
              component="a"
              href="https://facebook.com/profile.php?id=61577947200300"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FacebookIcon sx={{ color: "white" }} />
            </IconButton>
          </Grid>
          <Grid item sx={{ px: 1 }}>
            <Typography
              variant="h6"
              sx={{
                border: "1px solid white",
                padding: ".1rem .5rem !important",
                borderRadius: ".25rem!important",
                color: "white",
                fontSize: "1rem!important",
                margin: "1rem",
              }}
            >
              By authority: Friends of Mark Brewster; Juna Goldstein, Treasurer
            </Typography>
          </Grid>
        </Grid>
      </footer>

      <EmailSignupModal
        open={emailOpen}
        handleClose={() => setEmailOpen(false)}
      />
    </Box>
  );
};

export default Endorsements;